import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";
import { useCopy } from "@/lib/copyVariant";

// Variant-aware FAQ. Question/answer pairs come from copy variants so each
// audience variation can keep its own voice. `useCopy().c("faqItems")`
// returns an array of { q, a } strings.

export default function FAQSection() {
  const { c } = useCopy();
  const items = c("faqItems") || [];
  const [open, setOpen] = useState(0);

  if (!items.length) return null;

  return (
    <section
      id="faq"
      className="relative bg-[#fff8f3] py-16 sm:py-24 md:py-32 px-5 md:px-10"
      data-testid="faq-section"
    >
      <div className="max-w-[1100px] mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <motion.span
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            className="text-[10px] sm:text-[11px] uppercase tracking-[0.5em] text-[#96aeb4] font-semibold block mb-4"
          >
            {c("faqEyebrow")}
          </motion.span>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="font-serif text-3xl sm:text-4xl md:text-5xl font-bold text-[#464646] leading-[1.08] tracking-tight"
          >
            {c("faqHeadingL1")}
            <br />
            <span className="italic text-[#7a9d83]">{c("faqHeadingL2")}</span>
          </motion.h2>
        </div>

        <div className="divide-y divide-[#464646]/12 border-t border-b border-[#464646]/12">
          {items.map((item, i) => {
            const isOpen = open === i;
            return (
              <FAQRow
                key={i}
                index={i}
                item={item}
                isOpen={isOpen}
                onToggle={() => setOpen(isOpen ? -1 : i)}
              />
            );
          })}
        </div>

        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="mt-10 sm:mt-12 text-center text-sm text-[#464646]/60"
        >
          {c("faqFooter")}{" "}
          <a
            href="#contact"
            className="text-[#7a9d83] hover:text-[#464646] underline-offset-4 hover:underline transition-colors font-medium"
            data-testid="faq-contact-link"
          >
            {c("faqFooterCta")}
          </a>
        </motion.p>
      </div>
    </section>
  );
}

function FAQRow({ index, item, isOpen, onToggle }) {
  return (
    <div
      data-testid={`faq-item-${index}`}
      data-state={isOpen ? "open" : "closed"}
      className="group"
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={isOpen}
        className="w-full text-left py-6 sm:py-7 flex items-center gap-6 transition-colors hover:text-[#464646]"
        data-testid={`faq-toggle-${index}`}
      >
        <span className="text-[11px] font-mono text-[#96aeb4] w-8 shrink-0 tracking-widest">
          {String(index + 1).padStart(2, "0")}
        </span>
        <span className="flex-1 font-serif text-lg sm:text-xl md:text-2xl text-[#464646] font-medium leading-snug">
          {item.q}
        </span>
        <span className="w-9 h-9 sm:w-10 sm:h-10 rounded-full border border-[#464646]/20 flex items-center justify-center shrink-0 transition-colors group-hover:border-[#7a9d83] group-hover:text-[#7a9d83]">
          {isOpen ? <Minus size={16} /> : <Plus size={16} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
            className="overflow-hidden"
          >
            <div className="pl-14 pr-10 pb-7 sm:pb-8 text-sm sm:text-base text-[#464646]/75 leading-relaxed max-w-3xl">
              {item.a}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
